/**
 * Generates a cryptographically secure random string
 * Uses crypto.getRandomValues() as a secure alternative to Math.random()
 * Returns a base36 string similar to Math.random().toString(36).substr(2, length)
 */
export function generateSecureRandomString(length: number = 11): string {
  // Use crypto.getRandomValues as the primary secure method
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.getRandomValues) {
    // Generate enough random bytes to create the desired length string
    // Each byte in base36 gives us 1-2 characters, so we generate a bit more
    const bytesNeeded = Math.ceil(length * 1.5);
    const array = new Uint8Array(bytesNeeded);
    globalThis.crypto.getRandomValues(array);
    
    // Convert to base36 string (similar to Math.random().toString(36))
    // Filter out values that would produce invalid base36 characters
    let result = '';
    for (let i = 0; i < array.length && result.length < length; i++) {
      // Convert to base36 and ensure we get valid characters
      const char = array[i].toString(36);
      if (char.length > 0) {
        result += char;
      }
    }
    
    // If we don't have enough characters, pad with more random bytes
    while (result.length < length) {
      const additionalBytes = new Uint8Array(1);
      globalThis.crypto.getRandomValues(additionalBytes);
      const char = additionalBytes[0].toString(36);
      if (char.length > 0) {
        result += char;
      }
    }
    
    return result.slice(0, length);
  }

  // Last resort: if we're in a Node.js environment, use crypto module
  if (typeof require !== 'undefined') {
    try {
      const crypto = require('crypto');
      // Generate random bytes and convert to base36
      const bytes = crypto.randomBytes(Math.ceil(length * 1.5));
      let result = '';
      for (let i = 0; i < bytes.length && result.length < length; i++) {
        const char = bytes[i].toString(36);
        if (char.length > 0) {
          result += char;
        }
      }
      return result.slice(0, length);
    } catch {
      // Fall through to error
    }
  }

  // If no secure random is available, throw an error rather than using Math.random()
  throw new Error('No secure random number generator available');
}

/**
 * Generates a secure random ID suitable for session IDs
 * Returns a UUID if available, otherwise a secure random string
 */
export function generateSecureId(): string {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  // Generate a secure random string with timestamp for uniqueness
  const timestamp = Date.now();
  const randomPart = generateSecureRandomString(11);
  return `${timestamp}-${randomPart}`;
}

